/*
 * ELTANE — hero carousel
 *
 * Progressive enhancement for the multi-banner hero. The first slide is marked
 * is-active in Liquid and every slide is real markup, so with JS unavailable the
 * hero still paints its first banner; this controller only moves that class.
 *
 * Markup contract (see sections/eltane-hero.liquid):
 *   [data-eltane-hero]              root; data-autoplay ("true"/"false"), data-interval (ms)
 *     [data-eltane-hero-slide]      one per banner; .is-active is the visible one
 *     [data-eltane-hero-dot="N"]    0-indexed jump buttons
 *     [data-eltane-hero-prev]       step back one slide
 *     [data-eltane-hero-next]       step forward one slide
 *
 * Manual navigation is the dots, the prev/next arrows, or Left/Right while
 * the section has focus — any of them permanently stops the autoplay timer
 * (see stop()), same as a dot click always has.
 */
(function () {
  'use strict';

  function Carousel(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-eltane-hero-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-eltane-hero-dot]'));
    this.prevButton = root.querySelector('[data-eltane-hero-prev]');
    this.nextButton = root.querySelector('[data-eltane-hero-next]');
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    }));
    this.interval = parseInt(root.getAttribute('data-interval'), 10) || 7000;
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.timer = null;
    this.stopped = false;

    this.bind();
    this.go(this.index);
    if (this.autoplay) this.start();
  }

  Carousel.prototype.bind = function () {
    var self = this;

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.go(parseInt(dot.getAttribute('data-eltane-hero-dot'), 10) || 0);
        self.stop();
      });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', function () {
        self.step(-1);
        self.stop();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.step(1);
        self.stop();
      });
    }

    /* Autoplay must not steal a slide while someone is reading or tabbing
       through the CTAs — but mouseenter/mouseleave also fire when the page
       is scrolled under a stationary cursor (the hero is full-viewport, so
       any scroll drags it under, then out from under, wherever the mouse
       already sits). That synthetic pair paused the carousel on scroll-down
       and never resumed it on scroll-back-up, since a scroll rarely re-lands
       the cursor exactly back on the section. A real hover always carries
       nonzero movementX/Y (the pointer had to travel there); a scroll-
       synthesized one reports (0, 0) since the pointer itself never moved —
       that's what tells the two apart. */
    function isRealHover(event) {
      return event.movementX !== 0 || event.movementY !== 0;
    }
    this.root.addEventListener('mouseenter', function (event) { if (isRealHover(event)) self.pause(); });
    this.root.addEventListener('mouseleave', function (event) { if (isRealHover(event)) self.resume(); });
    this.root.addEventListener('focusin', function () { self.pause(); });
    this.root.addEventListener('focusout', function () { self.resume(); });

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { self.step(-1); self.stop(); }
      else if (event.key === 'ArrowRight') { self.step(1); self.stop(); }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
      else self.resume();
    });
  };

  Carousel.prototype.go = function (index) {
    var total = this.slides.length;
    this.index = ((index % total) + total) % total;

    for (var i = 0; i < total; i++) {
      var active = i === this.index;
      this.slides[i].classList.toggle('is-active', active);
      if (active) this.slides[i].removeAttribute('aria-hidden');
      else this.slides[i].setAttribute('aria-hidden', 'true');

      if (this.dots[i]) {
        this.dots[i].classList.toggle('is-active', active);
        this.dots[i].setAttribute('aria-selected', active ? 'true' : 'false');
      }
    }
  };

  Carousel.prototype.step = function (direction) {
    this.go(this.index + direction);
  };

  Carousel.prototype.start = function () {
    var self = this;
    this.pause();
    this.timer = window.setInterval(function () { self.step(1); }, this.interval);
  };

  Carousel.prototype.pause = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  /* stop() is permanent: once a visitor takes control, rotation does not
     resume on the next mouseleave. */
  Carousel.prototype.stop = function () {
    this.stopped = true;
    this.pause();
  };

  Carousel.prototype.resume = function () {
    if (this.autoplay && !this.stopped && !this.timer && !document.hidden) this.start();
  };

  function init(root) {
    (root || document).querySelectorAll('[data-eltane-hero]').forEach(function (node) {
      if (node.dataset.eltaneHeroBound === 'true') return;
      node.dataset.eltaneHeroBound = 'true';
      new Carousel(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
